/**
 * Superadmin-only. Live playback-incident forensics for a single venue.
 *
 * Wired to GET /api/analytics/admin/venue/forensics?email=<email>&hours=48.
 * Purpose: when a venue calls "playback stops after every song", we can
 * type their email in here and instantly see what their tablet's
 * telemetry is saying — which stall reason is firing, which UA the
 * device is running, and whether the JS ErrorBoundary is catching
 * anything. Before this page, the fastest way was mongosh.
 */
import React, { useState, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Loader2, Search, AlertTriangle, Monitor, Activity, Bug } from 'lucide-react';
import { toast } from 'sonner';
import BrokenTracksSection from '../components/BrokenTracksSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const relTime = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!t) return '—';
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const fmtUA = (ua) => {
  if (!ua) return 'unknown';
  const parts = [];
  if (/Android/i.test(ua)) parts.push('Android');
  else if (/iPhone|iPad|iOS/i.test(ua)) parts.push('iOS');
  else if (/Windows/i.test(ua)) parts.push('Windows');
  else if (/Macintosh|Mac OS/i.test(ua)) parts.push('macOS');
  if (/Chrome\/[\d.]+/i.test(ua)) parts.push('Chrome');
  else if (/Safari/i.test(ua)) parts.push('Safari');
  else if (/Firefox/i.test(ua)) parts.push('Firefox');
  const modelMatch = ua.match(/;\s*([^;)]+?)\s+Build\//);
  if (modelMatch) parts.push(modelMatch[1]);
  return parts.length ? parts.join(' · ') : ua.slice(0, 60);
};

const AdminForensicsPage = () => {
  const [email, setEmail] = useState('');
  const [hours, setHours] = useState(48);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const search = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setData(null);
    try {
      const res = await axios.get(`${API}/analytics/admin/venue/forensics`, {
        params: { email: email.trim(), hours },
      });
      setData(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Lookup failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [email, hours]);

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 max-w-6xl mx-auto space-y-6">
        <header className="space-y-1">
          <h1 className="text-2xl lg:text-3xl font-extrabold text-white" style={{ fontFamily: 'Outfit' }}>
            Playback Forensics
          </h1>
          <p className="text-sm text-white/50">
            Look up a venue by email to see what their tablet is reporting — stalls, JS errors, and device UAs.
          </p>
        </header>

        <BrokenTracksSection />

        <form onSubmit={search} className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="forensics-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="venue@example.com"
            className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#FF007F]"
            required
          />
          <select
            data-testid="forensics-hours-select"
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#FF007F]"
          >
            <option value={2}>Last 2h</option>
            <option value={24}>Last 24h</option>
            <option value={48}>Last 48h</option>
            <option value={168}>Last 7d</option>
            <option value={720}>Last 30d</option>
          </select>
          <Button
            type="submit"
            disabled={loading || !email.trim()}
            data-testid="forensics-search-btn"
            className="bg-[#FF007F] hover:bg-[#FF007F]/90 text-white font-bold px-6"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span className="ml-2">Look up</span>
          </Button>
        </form>

        {data && (
          <div className="space-y-6" data-testid="forensics-results">
            {/* Host summary */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <h2 className="text-lg font-bold text-white">{data.host?.name || 'Unknown host'}</h2>
                <span className="text-xs font-mono text-white/40">{data.host?.email}</span>
              </div>
              <div className="text-xs text-white/50 mt-1">
                {data.host?.venue_name || '—'} {data.host?.venue_code ? `· ${data.host.venue_code}` : ''}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                <div className="rounded-xl bg-white/[0.03] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Stalls</div>
                  <div className="text-2xl font-extrabold text-[#FF3B3B] mt-1" data-testid="forensics-stalls-total">
                    {data.stalls?.total ?? 0}
                  </div>
                </div>
                <div className="rounded-xl bg-white/[0.03] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">JS Errors</div>
                  <div className="text-2xl font-extrabold text-[#FFB020] mt-1" data-testid="forensics-errors-total">
                    {data.errors?.total ?? 0}
                  </div>
                </div>
                <div className="rounded-xl bg-white/[0.03] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Devices</div>
                  <div className="text-2xl font-extrabold text-white mt-1">
                    {data.devices?.length ?? 0}
                  </div>
                </div>
                <div className="rounded-xl bg-white/[0.03] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Window</div>
                  <div className="text-2xl font-extrabold text-white mt-1">{data.window_hours}h</div>
                </div>
              </div>
            </div>

            {/* Stall reasons */}
            <section>
              <h3 className="text-sm font-bold text-white/80 uppercase tracking-widest mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-[#FF3B3B]" />
                Stalls by reason
              </h3>
              {(data.stalls?.by_reason || []).length === 0 ? (
                <p className="text-sm text-white/40">No stalls recorded in this window.</p>
              ) : (
                <div className="rounded-xl border border-white/10 divide-y divide-white/5 overflow-hidden">
                  {data.stalls.by_reason.map((r) => (
                    <div key={r.reason} className="flex items-center justify-between px-4 py-3 bg-white/[0.02]">
                      <span className="font-mono text-sm text-white">{r.reason}</span>
                      <div className="flex items-center gap-4">
                        <span className="text-xs text-white/40">{relTime(r.last_at)}</span>
                        <span className="min-w-[3ch] text-right font-bold text-[#FF3B3B]">{r.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Devices */}
            <section>
              <h3 className="text-sm font-bold text-white/80 uppercase tracking-widest mb-3 flex items-center gap-2">
                <Monitor className="w-4 h-4 text-white/60" />
                Devices seen
              </h3>
              {(data.devices || []).length === 0 ? (
                <p className="text-sm text-white/40">No device UAs recorded.</p>
              ) : (
                <div className="rounded-xl border border-white/10 divide-y divide-white/5 overflow-hidden">
                  {data.devices.map((d, i) => (
                    <div key={i} className="px-4 py-3 bg-white/[0.02]">
                      <div className="text-sm font-semibold text-white">{fmtUA(d.user_agent)}</div>
                      <div className="text-[11px] text-white/40 mt-0.5 font-mono truncate">{d.user_agent}</div>
                      <div className="text-xs text-white/50 mt-1">
                        {d.sample_count} sample{d.sample_count === 1 ? '' : 's'} · last {relTime(d.last_seen)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Recent stall events */}
            <section>
              <h3 className="text-sm font-bold text-white/80 uppercase tracking-widest mb-3 flex items-center gap-2">
                <Activity className="w-4 h-4 text-white/60" />
                Recent stall events ({data.stalls?.recent?.length || 0})
              </h3>
              {(data.stalls?.recent || []).length === 0 ? (
                <p className="text-sm text-white/40">No recent stalls.</p>
              ) : (
                <div className="rounded-xl border border-white/10 divide-y divide-white/5 max-h-[500px] overflow-y-auto">
                  {data.stalls.recent.map((s, i) => (
                    <div key={i} className="px-4 py-3 bg-white/[0.02]">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs text-[#FF3B3B]">{s.reason}</span>
                        <span className="text-[11px] text-white/40">{relTime(s.at)}</span>
                      </div>
                      {s.is_hidden && (
                        <span className="inline-block mt-1 text-[10px] uppercase tracking-widest bg-[#FFB020]/15 text-[#FFB020] px-2 py-0.5 rounded">
                          Hidden tab
                        </span>
                      )}
                      {s.meta && Object.keys(s.meta).length > 0 && (
                        <pre className="text-[11px] text-white/50 font-mono mt-2 whitespace-pre-wrap break-all">
{JSON.stringify(s.meta, null, 0)}
                        </pre>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Frontend errors */}
            <section>
              <h3 className="text-sm font-bold text-white/80 uppercase tracking-widest mb-3 flex items-center gap-2">
                <Bug className="w-4 h-4 text-white/60" />
                Recent JS errors ({data.errors?.recent?.length || 0})
              </h3>
              {(data.errors?.recent || []).length === 0 ? (
                <p className="text-sm text-white/40">No JS errors caught by the boundary. 👍</p>
              ) : (
                <div className="rounded-xl border border-white/10 divide-y divide-white/5 max-h-[500px] overflow-y-auto">
                  {data.errors.recent.map((e, i) => (
                    <div key={i} className="px-4 py-3 bg-white/[0.02]">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-[#FFB020] truncate mr-3">{e.message}</span>
                        <span className="text-[11px] text-white/40 whitespace-nowrap">{relTime(e.at)}</span>
                      </div>
                      <div className="text-[11px] text-white/40 mt-0.5">at {e.path || '—'}</div>
                      {e.stack_head && (
                        <pre className="text-[11px] text-white/40 font-mono mt-2 whitespace-pre-wrap break-all">{e.stack_head}</pre>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminForensicsPage;
